import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend
} from 'recharts';
import { X, Printer, FileText, Table2, BarChart3 } from 'lucide-react';

// Color palette for multi-line charts
const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export default function PrintGraphModal({
  isOpen,
  onClose,
  studentName,
  interventionName,
  chartData,
  tableData,
  chartType = 'single', // 'single' or 'multi'
  interventions = [], // For multi-line charts
  baseline,
  goal,
  scoreUnit = '%'
}) {
  const printRef = useRef(null);
  const [printMode, setPrintMode] = useState('graph'); // 'graph' or 'graph-table'
  const [isPrinting, setIsPrinting] = useState(false);

  if (!isOpen) return null;

  const handlePrint = () => {
    setIsPrinting(true);
    
    // Small delay to ensure state updates render
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      {/* Print Options Modal */}
      <div className="modal-overlay print:hidden" onClick={onClose}>
        <div className="modal-content print:hidden" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h2 className="modal-title flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Print Options
            </h2>
            <button className="modal-close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>

          <div className="modal-body space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-700 mb-1">Student</h3>
              <p className="text-lg font-semibold text-slate-900">{studentName}</p>
              {interventionName && (
                <>
                  <h3 className="text-sm font-medium text-slate-700 mt-3 mb-1">Intervention</h3>
                  <p className="text-base text-slate-800">{interventionName}</p>
                </>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                What would you like to print?
              </label>
              <div className="space-y-2">
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    printMode === 'graph' 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="printMode"
                    value="graph"
                    checked={printMode === 'graph'}
                    onChange={(e) => setPrintMode(e.target.value)}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <BarChart3 className="h-5 w-5 text-slate-600" />
                  <div>
                    <div className="font-medium text-slate-900">Graph Only</div>
                    <div className="text-sm text-slate-500">Print the chart with student info header</div>
                  </div>
                </label>

                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    printMode === 'graph-table' 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="printMode"
                    value="graph-table"
                    checked={printMode === 'graph-table'}
                    onChange={(e) => setPrintMode(e.target.value)}
                    className="w-4 h-4 text-indigo-600"
                  />
                  <Table2 className="h-5 w-5 text-slate-600" />
                  <div>
                    <div className="font-medium text-slate-900">Graph + Data Table</div>
                    <div className="text-sm text-slate-500">Print the chart with associated data values</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> For best results, use "Save as PDF" in the print dialog for digital sharing.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handlePrint}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Printable Content - Hidden on screen, visible when printing */}
      <div className="print-content" id="print-graph-content" ref={printRef}>
        {/* Print Header */}
        <div className="print-header">
          <div className="print-header-main">
            <h1 className="print-title">MTSS Progress Tracker</h1>
            <p className="print-subtitle">Data Report</p>
          </div>
          <div className="print-date">
            Generated: {currentDate}
          </div>
        </div>

        {/* Student Info */}
        <div className="print-student-info">
          <div className="print-info-row">
            <span className="print-label">Student:</span>
            <span className="print-value">{studentName}</span>
          </div>
          {interventionName && (
            <div className="print-info-row">
              <span className="print-label">Intervention:</span>
              <span className="print-value">{interventionName}</span>
            </div>
          )}
          {baseline && (
            <div className="print-info-row">
              <span className="print-label">Baseline:</span>
              <span className="print-value">{baseline} {scoreUnit}</span>
            </div>
          )}
          {goal && (
            <div className="print-info-row">
              <span className="print-label">Goal:</span>
              <span className="print-value">{goal} {scoreUnit}</span>
            </div>
          )}
        </div>

        {/* Chart - Using fixed width for print compatibility */}
        <div className="print-chart-container">
          <h2 className="print-section-title">
            {chartType === 'multi' ? 'Data Comparison: All Interventions' : `Data Over Time${interventionName ? `: ${interventionName}` : ''}`}
          </h2>
          <div className="print-chart" style={{ width: '100%', height: 350 }}>
            {chartType === 'single' ? (
              <LineChart 
                width={680} 
                height={320} 
                data={chartData} 
                margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11, fill: '#374151' }}
                  tickMargin={10}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#374151' }}
                  tickMargin={10}
                  label={{ value: scoreUnit, angle: -90, position: 'insideLeft', fontSize: 12 }}
                />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#4f46e5" 
                  strokeWidth={2}
                  dot={{ fill: '#4f46e5', r: 5 }}
                />
                {goal && (
                  <ReferenceLine 
                    y={goal} 
                    stroke="#10b981" 
                    strokeDasharray="5 5"
                    label={{ value: `Goal: ${goal}`, fill: '#10b981', fontSize: 11, position: 'right' }}
                  />
                )}
                {baseline && (
                  <ReferenceLine 
                    y={baseline} 
                    stroke="#f59e0b" 
                    strokeDasharray="5 5"
                    label={{ value: `Baseline: ${baseline}`, fill: '#f59e0b', fontSize: 11, position: 'right' }}
                  />
                )}
              </LineChart>
            ) : (
              <LineChart 
                width={680} 
                height={320} 
                data={chartData} 
                margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11, fill: '#374151' }}
                  tickMargin={10}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#374151' }}
                  tickMargin={10}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '9px' }} />
                {interventions.map((intervention, index) => (
                  <Line
                    key={intervention.id}
                    type="monotone"
                    dataKey={intervention.id}
                    name={intervention.intervention_name}
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS[index % CHART_COLORS.length], r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            )}
          </div>
        </div>

        {/* Data Table - Only shown in graph-table mode */}
        {printMode === 'graph-table' && tableData && tableData.length > 0 && (
          <div className="print-table-container">
            <h2 className="print-section-title">Data Points</h2>
            <table className="print-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Score</th>
                  <th>Unit</th>
                  {tableData[0]?.intervention_name && <th>Intervention</th>}
                  {tableData[0]?.note && <th>Notes</th>}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.date_collected || row.date}</td>
                    <td className="text-center font-medium">{row.score}</td>
                    <td className="text-center">{row.score_unit || scoreUnit}</td>
                    {row.intervention_name && <td>{row.intervention_name}</td>}
                    {row.note !== undefined && <td className="text-sm">{row.note || '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Print Footer */}
        <div className="print-footer">
          <p>MTSS Progress Tracker • Confidential Student Data</p>
        </div>
      </div>
    </>
  );
}
